import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CalendarDays, ClipboardList, Plus, Search, X } from 'lucide-react'
import { getApiErrorMessage } from '@/api/client'
import { useEngineers } from '@/api/engineers'
import { useCreateMaintenanceOrder, useMaintenanceOrders } from '@/api/maintenance'
import {
  MAINTENANCE_STATUS_LABEL,
  MAINTENANCE_TYPE_LABEL,
  type MaintenanceOrderStatus,
  type MaintenanceType,
} from '@/api/types/maintenance'
import { useAuth } from '@/auth/AuthProvider'
import { InlineError } from '@/components/ErrorDisplay'
import { useToast } from '@/components/ToastProvider'
import { useDeviceAssets } from '@/hooks/asset'

type StatusFilter = MaintenanceOrderStatus | 'ALL'

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'ALL', label: '全部' },
  { value: 'SCHEDULED', label: '待安排' },
  { value: 'ASSIGNED', label: '已指派' },
  { value: 'IN_PROGRESS', label: '保养中' },
  { value: 'PENDING_CONFIRMATION', label: '待确认' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'CANCELLED', label: '已取消' },
]

const STATUS_STYLE: Record<MaintenanceOrderStatus, string> = {
  SCHEDULED: 'bg-gray-100 text-gray-700',
  ASSIGNED: 'bg-blue-50 text-blue-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-700',
  PENDING_CONFIRMATION: 'bg-purple-50 text-purple-700',
  COMPLETED: 'bg-green-50 text-green-700',
  CANCELLED: 'bg-red-50 text-red-700',
}

export function MaintenanceStatusBadge({ status }: { status: MaintenanceOrderStatus }) {
  return (
    <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[status]}`}>
      {MAINTENANCE_STATUS_LABEL[status]}
    </span>
  )
}

function today() {
  const current = new Date()
  current.setMinutes(current.getMinutes() - current.getTimezoneOffset())
  return current.toISOString().slice(0, 10)
}

function CreateOrderModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const devices = useDeviceAssets()
  const engineers = useEngineers()
  const createOrder = useCreateMaintenanceOrder()
  const [deviceId, setDeviceId] = useState('')
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>('ANNUAL')
  const [scheduledDate, setScheduledDate] = useState(today())
  const [formName, setFormName] = useState('医疗设备定期保养单')
  const [formVersion, setFormVersion] = useState('')
  const [engineerId, setEngineerId] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!deviceId) {
      toast.error('请选择保养设备')
      return
    }
    const engineer = engineers.data?.find((item) => item.userId === engineerId)
    try {
      const created = await createOrder.mutateAsync({
        deviceId: Number(deviceId),
        maintenanceType,
        scheduledDate,
        formName: formName.trim(),
        formVersion: formVersion.trim() || undefined,
        assignedEngineerId: engineer?.userId,
        assignedEngineerName: engineer?.userName,
      })
      toast.success('维保工单已创建')
      navigate(`/maintenance-orders/${created.id}`)
    } catch (error) {
      toast.error(getApiErrorMessage(error))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">新建维保工单</h2>
            <p className="mt-1 text-xs text-gray-500">建立基础信息，现场完整保养单后续以照片上传。</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <label className="sm:col-span-2 text-sm font-medium text-gray-700">
            保养设备 <span className="text-red-500">*</span>
            <select required value={deviceId} onChange={(e) => setDeviceId(e.target.value)} className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal focus:ring-2 focus:ring-primary-500">
              <option value="">请选择设备</option>
              {devices.data?.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.deviceName} · {device.hospitalName || device.customerName || '未绑定客户'} · {device.assetCode}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            保养类型 <span className="text-red-500">*</span>
            <select value={maintenanceType} onChange={(e) => setMaintenanceType(e.target.value as MaintenanceType)} className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal">
              {Object.entries(MAINTENANCE_TYPE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            计划日期 <span className="text-red-500">*</span>
            <input required type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal" />
          </label>
          <label className="text-sm font-medium text-gray-700">
            保养单名称 <span className="text-red-500">*</span>
            <input required value={formName} onChange={(e) => setFormName(e.target.value)} className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal" />
          </label>
          <label className="text-sm font-medium text-gray-700">
            表单版本
            <input value={formVersion} onChange={(e) => setFormVersion(e.target.value)} placeholder="例如 V1.0" className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal" />
          </label>
          <label className="sm:col-span-2 text-sm font-medium text-gray-700">
            指派工程师
            <select value={engineerId} onChange={(e) => setEngineerId(e.target.value)} className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 font-normal">
              <option value="">暂不指派</option>
              {engineers.data?.map((engineer) => <option key={engineer.userId} value={engineer.userId}>{engineer.userName}（{engineer.userId}）</option>)}
            </select>
          </label>
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">取消</button>
          <button disabled={createOrder.isPending} className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
            {createOrder.isPending ? '创建中...' : '创建工单'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function MaintenanceOrderList() {
  const { session } = useAuth()
  const canCreate = session?.role === 'ADMIN' || session?.role === 'STATION_MANAGER'
  const [status, setStatus] = useState<StatusFilter>('ALL')
  const [inputValue, setInputValue] = useState('')
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const orders = useMaintenanceOrders({ status: status === 'ALL' ? undefined : status, keyword: keyword || undefined, page, size: 20 })

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">维保工单</h1>
          <p className="mt-1 text-sm text-gray-500">跟踪定期保养执行状态，完整纸质保养单以照片归档。</p>
        </div>
        {canCreate && <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700"><Plus className="h-4 w-4" />新建维保工单</button>}
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((item) => <button key={item.value} onClick={() => { setStatus(item.value); setPage(0) }} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${status === item.value ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{item.label}</button>)}
          <div className="flex-1" />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setKeyword(inputValue); setPage(0) } }} placeholder="工单号 / 医院 / 设备" className="w-64 rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm" />
          </div>
          <button onClick={() => { setKeyword(inputValue); setPage(0) }} className="rounded-lg bg-gray-900 px-4 py-2 text-sm text-white">搜索</button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {orders.error && <div className="p-5"><InlineError message={getApiErrorMessage(orders.error)} /></div>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500"><tr><th className="px-5 py-3">维保工单号</th><th className="px-5 py-3">客户 / 医院</th><th className="px-5 py-3">设备</th><th className="px-5 py-3">保养类型</th><th className="px-5 py-3">计划日期</th><th className="px-5 py-3">工程师</th><th className="px-5 py-3">结果</th><th className="px-5 py-3">状态</th><th className="px-5 py-3">操作</th></tr></thead>
            <tbody>
              {orders.isLoading && <tr><td colSpan={9} className="px-5 py-14 text-center text-gray-400">正在加载维保工单...</td></tr>}
              {orders.data?.content.map((order) => (
                <tr key={order.id} className="border-b border-gray-100 text-sm hover:bg-gray-50">
                  <td className="px-5 py-4"><Link to={`/maintenance-orders/${order.id}`} className="font-mono font-medium text-primary-600 hover:underline">{order.orderNo}</Link></td>
                  <td className="px-5 py-4"><div className="font-medium text-gray-900">{order.hospitalName || order.customerName || '-'}</div>{order.department && <div className="mt-0.5 text-xs text-gray-400">{order.department}</div>}</td>
                  <td className="px-5 py-4"><div className="text-gray-900">{order.deviceName}</div><div className="mt-0.5 text-xs text-gray-400">{order.deviceModel || order.deviceSerialNo || '-'}</div></td>
                  <td className="px-5 py-4 text-gray-600">{MAINTENANCE_TYPE_LABEL[order.maintenanceType]}</td>
                  <td className="px-5 py-4"><div className={order.overdue ? 'font-medium text-red-600' : 'text-gray-600'}>{order.scheduledDate}</div>{order.overdue && <span className="text-xs text-red-500">已逾期</span>}</td>
                  <td className="px-5 py-4 text-gray-600">{order.assignedEngineerName || '未指派'}</td>
                  <td className="px-5 py-4"><span className={order.overallResult === 'ABNORMAL' ? 'text-red-600' : order.overallResult === 'PASS' ? 'text-green-600' : 'text-gray-400'}>{order.overallResult === 'ABNORMAL' ? '异常' : order.overallResult === 'PASS' ? '正常' : '-'}</span></td>
                  <td className="px-5 py-4"><MaintenanceStatusBadge status={order.status} /></td>
                  <td className="px-5 py-4"><Link to={`/maintenance-orders/${order.id}`} className="text-primary-600 hover:underline">查看详情</Link></td>
                </tr>
              ))}
              {!orders.isLoading && !orders.error && orders.data?.content.length === 0 && <tr><td colSpan={9} className="px-5 py-16"><div className="flex flex-col items-center gap-3 text-gray-400"><ClipboardList className="h-12 w-12 text-gray-300" />暂无维保工单</div></td></tr>}
            </tbody>
          </table>
        </div>
        {orders.data && orders.data.totalPages > 1 && <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4 text-sm"><span className="text-gray-500">共 {orders.data.totalElements} 条</span><div className="flex items-center gap-3"><button disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))} className="rounded border px-3 py-1 disabled:opacity-40">上一页</button><span>{page + 1} / {orders.data.totalPages}</span><button disabled={page >= orders.data.totalPages - 1} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-40">下一页</button></div></div>}
      </div>
      {createOpen && <CreateOrderModal onClose={() => setCreateOpen(false)} />}
    </div>
  )
}
